import { Box, Divider, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import classNames from 'classnames';
import React, { FC, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isEmpty, isEqual } from 'lodash';
import Button from '../../components/Button/Button';
import { FormattedMessage, IconGemFilled, NamedLink, SearchResultsPanel } from '../../components';
import Banner from '../../components/Banner/Banner';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { setSavedSearchSource } from '../../ducks/user.duck';
import { useEnabledCustomerExperiences } from '../../hooks/useEnabledCustomerExperiences';
import { EmailSubscribeSource } from '../../types/apollo/generated/types.generated';
import { parse } from '../../util/urlHelpers';
import AppContext from '../../context/AppContext';
import { useAllowedFindItemMethods } from '../../hooks/useAllowedFindItemMethods';
import { ModalType, setActiveModal } from '../../ducks/modal.duck';
import { LandingPageV2State } from './LandingPageV2.duck';
import { trackClickISOBanner, trackViewSearchResults } from '../../util/heap';
import TypographyWrapper, {
  TypographyFormat,
  TypographyWeight,
} from '../../components/TypographyWrapper/TypographyWrapper';
import { FrenzyApiModes } from '../../types/frenzy/query';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import RecentArrivalsCarousel from '../../components/RecentArrivalsCarousel/RecentArrivalsCarousel';
import { RecentArrivalsCarouselState } from '../../ducks/recentArrivalsCarousel.duck';
import { useIsShopSideLaunched } from '../../hooks/useIsShopSideLaunched';
import { MIN_CAROUSEL_LISTINGS } from '../../components/ListingItemsCarousel/ListingItemsCarousel';
import { useZeroResultsFromUrlChangeSentryLogging } from './hooks/useZeroResultsSentryLogging';
import css from './LandingPageV2.module.css';

interface SearchPanelProps {
  listingsAreLoaded: boolean;
  totalItems: number;
  areFiltersApplied: boolean;
}

const RecentlyListedListingsSearchResults = () => {
  const { recentListings } = useSelector<any>(
    (state) => state.recentArrivalsCarousel
  ) as RecentArrivalsCarouselState;

  const isShopSideLaunched = useIsShopSideLaunched();

  // Render the divider only if the Recent Arrivals Carousel is displayed
  const displayDivider = isShopSideLaunched && recentListings.length >= MIN_CAROUSEL_LISTINGS;

  return (
    <Box>
      {displayDivider && (
        <Box my={3}>
          <Divider />
        </Box>
      )}
      <RecentArrivalsCarousel />
    </Box>
  );
};

interface SearchResultsBannerProps {
  areListingsLoaded: boolean;
  totalItems: number;
}

const SearchResultsBanner: FC<SearchResultsBannerProps> = ({ areListingsLoaded, totalItems }) => {
  const { treetId } = useContext(AppContext);
  const { shopName } = useShopConfig();
  const theme = useTheme();
  const isMediaQueryXsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const dispatch = useDispatch();
  const { shouldAllowISO } = useAllowedFindItemMethods();

  const { pagination, searchParams } = useSelector<any>(
    (state) => state.LandingPageV2
  ) as LandingPageV2State;

  const handleISOBannerClick = () => {
    trackClickISOBanner(treetId, shopName, pagination?.page || 1);
    dispatch(setActiveModal(ModalType.ISO));
    dispatch(setSavedSearchSource({ source: EmailSubscribeSource.SearchResultsBannerIso }));
  };

  const hasResults = areListingsLoaded && totalItems > 0;
  const hasNoResults = areListingsLoaded && totalItems === 0;

  if (
    areListingsLoaded &&
    shouldAllowISO &&
    (hasResults || (hasNoResults && isEmpty(searchParams?.keywords)))
  ) {
    return (
      <Banner
        bodyText="Looking for a specific item?"
        actionText="Submit an item request."
        icon={<IconGemFilled />}
        onActionClick={handleISOBannerClick}
        mb={isMediaQueryXsDown ? 1 : 2}
      />
    );
  }

  return null;
};

const SearchPanel: FC<SearchPanelProps> = ({
  listingsAreLoaded,
  totalItems,
  areFiltersApplied,
}) => {
  const rootState = useSelector<any>((state) => state) as any;
  const { currentPageResultIds, pagination, searchListingsError } = useSelector<any>(
    (state) => state.LandingPageV2
  ) as LandingPageV2State;
  const listings = getListingsById(rootState, currentPageResultIds);

  const { treetId } = useContext(AppContext);
  const { shopName } = useShopConfigV2();
  const { allowSell, isListTradeInOnly } = useEnabledCustomerExperiences();
  const theme = useTheme();
  const isMediaQueryXsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const location = useLocation();
  const dispatch = useDispatch();
  const { shouldAllowISO } = useAllowedFindItemMethods();

  const searchParams = parse(location.search);
  if (searchParams?.keywords && listingsAreLoaded) {
    trackViewSearchResults(totalItems, searchParams.keywords, treetId);
  }

  const handleISOBannerClick = () => {
    trackClickISOBanner(treetId, shopName, pagination?.page || 1);
    dispatch(setActiveModal(ModalType.ISO));
    dispatch(setSavedSearchSource({ source: EmailSubscribeSource.SearchResultsBannerIso }));
  };

  // Log to Sentry when filters produce zero results from URL
  useZeroResultsFromUrlChangeSentryLogging({
    listingsAreLoaded,
    totalItems,
    areFiltersApplied,
    searchParams,
    treetId,
    shopName,
  });

  const clearFiltersOrSearchLink = (
    <NamedLink
      name="LandingPage"
      to={{
        state: { scrollOnRender: true },
        search: 'mode=raw-query',
      }}
    >
      <TypographyWrapper
        variant="body1"
        format={TypographyFormat.Underlined}
        weight={TypographyWeight.Bold}
      >
        {searchParams.keywords ? 'Clear search' : 'Clear filters'}
      </TypographyWrapper>
    </NamedLink>
  );

  const listItemLink = (
    <NamedLink name="NewListingPage">
      <TypographyWrapper
        variant="body2"
        format={TypographyFormat.Underlined}
        typographyOverrides={{ display: 'inline' }}
      >
        {isListTradeInOnly ? 'trade in your item' : 'list your own item'}
      </TypographyWrapper>
    </NamedLink>
  );

  const hasResults = listingsAreLoaded && totalItems > 0;
  const hasNoResults = listingsAreLoaded && totalItems === 0;
  const shouldShowRecentArrivals = hasNoResults && !isEmpty(searchParams?.keywords);

  return (
    <>
      <Box px={isMediaQueryXsDown ? 2 : 8} py={isMediaQueryXsDown ? 1 : 2}>
        <div className={css.searchResultsWrapper}>
          {!!searchListingsError && (
            <h2 className={css.error}>
              <FormattedMessage id="SearchPage.searchError" />
            </h2>
          )}
          <SearchResultsBanner areListingsLoaded={listingsAreLoaded} totalItems={totalItems} />
          {hasNoResults && !areFiltersApplied && (
            <Box py={1}>
              <Typography variant="body2">
                {!allowSell ? (
                  'All items have recently sold! Check back soon.'
                ) : (
                  <FormattedMessage id="SearchFiltersPrimary.noResults" values={{ listItemLink }} />
                )}
              </Typography>
            </Box>
          )}
          {hasNoResults && areFiltersApplied && (
            <Box
              my={3}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
            >
              <Box my={1}>
                {searchParams.keywords && (
                  <>
                    <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
                      No results for{' '}
                    </TypographyWrapper>
                    <TypographyWrapper
                      variant="body1"
                      weight={TypographyWeight.Bold}
                      typographyOverrides={{ display: 'inline' }}
                      isDynamicValue
                    >
                      {`“${searchParams.keywords}”`}
                    </TypographyWrapper>
                  </>
                )}
                {!searchParams.keywords && (
                  <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
                    No results found.
                  </TypographyWrapper>
                )}
              </Box>
              {shouldAllowISO && (
                <>
                  <Box my={1}>
                    <TypographyWrapper variant="body1">
                      But you can still submit an item request! We’ll find the exact item you have
                      in mind and let you know when it lists!
                    </TypographyWrapper>
                  </Box>
                  <Box my={1}>
                    <Button onClick={handleISOBannerClick}>Submit An Item Request</Button>
                  </Box>
                </>
              )}
              {clearFiltersOrSearchLink}
            </Box>
          )}
          {hasResults && (
            <Box mb={2} display="flex" flexDirection="column" alignItems="start">
              {searchParams.keywords ? (
                <>
                  <Box>
                    <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
                      {`${totalItems} results for `}
                    </TypographyWrapper>
                    <TypographyWrapper
                      variant="body1"
                      typographyOverrides={{ display: 'inline' }}
                      weight={TypographyWeight.Bold}
                    >
                      {`“${searchParams.keywords}”`}
                    </TypographyWrapper>
                  </Box>
                  {clearFiltersOrSearchLink}
                </>
              ) : (
                searchParams.mode === FrenzyApiModes.FilterChange && (
                  <>
                    <Box>
                      <TypographyWrapper
                        variant="body1"
                        typographyOverrides={{ display: 'inline' }}
                      >
                        {`${totalItems} results`}
                      </TypographyWrapper>
                    </Box>
                    {clearFiltersOrSearchLink}
                  </>
                )
              )}
            </Box>
          )}
          <div
            className={classNames({
              [css.newSearchInProgress]: !listingsAreLoaded,
            })}
          >
            {shouldShowRecentArrivals && <RecentlyListedListingsSearchResults />}
            <SearchResultsPanel
              listings={listings}
              pagination={pagination}
              search={searchParams}
              areListingsLoaded={listingsAreLoaded}
            />
          </div>
        </div>
      </Box>
    </>
  );
};

const areEqual = (prevProps: SearchPanelProps, nextProps: SearchPanelProps) =>
  isEqual(prevProps, nextProps);

export default React.memo(SearchPanel, areEqual);
